//@flow

import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import merge from "lodash/merge";

import { getUser, getCategories } from "redux/sagas/selectors";

import { changeServiceFormInteraction } from "Commons/Utilities/GoogleTagManager";
import { getTrackingData } from "Commons/Utilities/Tracking/TrackingHandler";
import { createChangeService, resetState } from "modules/ChangeService/redux/actions";
import { fetchCategoryBySlug } from "redux/actions/category";
import { scrollToFormTop, normalizeData } from "modules/ChangeServiceForm/misc/utilities";
import { deviceMetadata } from "Commons/Utilities/getDeviceMetadata";

import type { SharedStateT } from "modules/ChangeServiceForm/misc/types";
import styleVersions from "./components/versions/styles";
import componentVersions from "./components/versions/components";
import dataVersions from "./components/versions/datas";
import { SharedStateProvider } from "./misc/context";

type Props = {
  componentName: string,
  styleName: string,
  dataName: string | Object,
  customization?: {
    data: string,
  },
};

const ChangeServiceForm = ({ componentName, styleName, dataName, customization = {} }: Props) => {
  const user = useSelector(getUser);
  const categories = useSelector(getCategories);
  const dispatch = useDispatch();
  const [step, setFormStep] = useState(1);
  const [categoryType, setCategoryType] = useState("");
  const [showLoginOrRegister, setShowLoginOrRegister] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totalSteps, setTotalSteps] = useState(0);
  const [stepName, setStepName] = useState("");

  /* Extracted versions */
  const ComponentVersion: function = componentVersions[componentName];
  const inheritedStyles = styleVersions[styleName];
  const configurationBase = dataVersions[dataName];

  /* Error handling */
  if (!ComponentVersion || !inheritedStyles || !configurationBase) {
    throw new Error(
      "Unable to load the correct versions of the component. Make sure that the string names of the props match the indexed version names and that are they are correctly registered in the indexes of each /version folders (see /components or /datas or /styles)"
    );
  }

  /* Customization */
  const customizationData = customization?.data ? JSON.parse(customization?.data) : {};
  const configurationData = merge({}, configurationBase, customizationData);

  /* Category initialization */
  const { initialValues } = configurationData;

  useEffect(() => {
    // Get both category details
    dispatch(fetchCategoryBySlug("strom"));
    dispatch(fetchCategoryBySlug("gas"));
  }, []);

  useEffect(() => {
    setCategoryType(initialValues.initialCategory);
  }, [initialValues.initialCategory]);

  const activeCategory = categories?.find((category) => category?.categoryType === categoryType);

  /* Steps */
  const stepsCount = configurationData.stepsDependencies.length;
  const loggedUser = user?.user;

  useEffect(() => {
    const totalSteps = !loggedUser ? stepsCount + 2 : stepsCount + 1;
    setTotalSteps(totalSteps);
    return () => {
      dispatch(resetState());
    };
  }, []);

  /* Final submit on confirmation step */
  const onFormFinalSubmit = async (values) => {
    setLoading(true);
    const trackingInfo = getTrackingData();
    try {
      await dispatch(
        createChangeService(
          {
            ...normalizeData(values),
            ...trackingInfo,
            categoryId: activeCategory.id,
            latestDeviceMetadata: deviceMetadata,
            signInUrl: window.location.pathname,
          },
          loggedUser
        )
      );
      setConfirmed(true);
      changeServiceFormInteraction("CS_DONE");
      setShowLoginOrRegister(false);
      scrollToFormTop(16);
      setFormStep(step + 1);
      setTimeout(() => {
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const sharedState: SharedStateT = {
    inheritedStyles,
    configurationData,
    category: { categoryType, setCategoryType },
    loginOrRegisterStep: { showLoginOrRegister, setShowLoginOrRegister },
    steps: { step, setFormStep, totalSteps, stepName, setStepName },
    onFormFinalSubmit,
    confirmation: { confirmed, setConfirmed },
    user,
    loading,
    activeCategory,
    initialValues,
  };

  return (
    <div className={inheritedStyles.container} id="csFormContainer">
      <SharedStateProvider value={sharedState}>
        <ComponentVersion />
      </SharedStateProvider>
    </div>
  );
};

export default ChangeServiceForm;
